import { useEffect, useRef, useState } from 'react';
import { lowerFirst } from 'lodash';
import { useFormikContext } from 'formik';
import { useTranslation } from 'react-i18next';

import { Alert, Button, FormControl, FormLabel, Option, Select } from '@mui/joy';

import { AccessKeyActionType, CaseType } from '~/common/enums';
import { type AccessKeyActionFormikValues } from '../hooks/useAccessKeyActionFormikConfig';
import { pagination } from '~/common/constants';
import { trpc } from '@/config/trpc';
import useToggle from '@/shared/hooks/UseToggle';

import { UserIcon, WarningIcon } from '@/shared/icons/Icons';
import AddAccessKeyActionSelectAccessKeysModal from './AddAccessKeyActionSelectAccessKeysModal';
import FormikCaseSelector from '@/shared/components/2.0/formik/FormikCaseSelector';
import FormikCrmSelectorJoy from '@/shared/components/2.0/formik/FormikCrmSelectorJoy';
import FormikDatePicker from '@/shared/components/2.0/formik/FormikDatePicker';
import FormikTextField from '@/shared/components/2.0/formik/FormikTextField';

type Props = {
  selectedAccessKeyInstanceId?: string;
  actionGroupId?: string;
};

const allowedActionTypes = [
  AccessKeyActionType.Reservation,
  AccessKeyActionType.Loan,
  AccessKeyActionType.Return,
];

const AddAccessKeyActionForm: React.FC<Props> = ({
  selectedAccessKeyInstanceId,
  actionGroupId,
}) => {
  const { t } = useTranslation();
  const { values, setFieldValue, setFieldTouched } =
    useFormikContext<AccessKeyActionFormikValues>();

  const [isSelectedAccessKeyInstanceAvailable, setIsSelectedAccessKeyInstanceAvailable] =
    useState(true);
  const hasAddedSelectedInstanceRef = useRef(false);

  const toggles = {
    selectAccessKeys: useToggle(),
  };

  const availableAccessKeyInstancesQuery = trpc.accessKeys.instances.available.useInfiniteQuery(
    {
      accessKeyInstanceId: selectedAccessKeyInstanceId,
      actionGroupId,
      actionType: values.actionType,
      buildingIds: [],
      buildingUnitIds: [],
      fromDate: values.fromDate,
      searchTerm: '',
      untilDate: values.untilDate,
    },
    {
      enabled: !!selectedAccessKeyInstanceId,
      getNextPageParam: (lastPage) => lastPage.nextPage,
      initialCursor: pagination.queryParam.FIRST_PAGE,
    },
  );

  useEffect(() => {
    hasAddedSelectedInstanceRef.current = false;
    setIsSelectedAccessKeyInstanceAvailable(true);
  }, [values.actionType, selectedAccessKeyInstanceId]);

  useEffect(() => {
    if (!availableAccessKeyInstancesQuery.data) {
      return;
    }
    if (!selectedAccessKeyInstanceId) {
      hasAddedSelectedInstanceRef.current = true;
      return;
    }

    if (hasAddedSelectedInstanceRef.current) {
      return;
    }

    const availableItems =
      availableAccessKeyInstancesQuery.data?.pages.flatMap((page) => page.items) ?? [];
    const instance = availableItems.find(
      (item) => item.accessKeyInstanceId === selectedAccessKeyInstanceId,
    );

    if (!instance) {
      setIsSelectedAccessKeyInstanceAvailable(false);
      hasAddedSelectedInstanceRef.current = true;
      return;
    }

    hasAddedSelectedInstanceRef.current = true;
    setFieldValue('accessKeys', [instance]);
  }, [availableAccessKeyInstancesQuery.data, selectedAccessKeyInstanceId, setFieldValue]);

  return (
    <>
      {!isSelectedAccessKeyInstanceAvailable && selectedAccessKeyInstanceId && (
        <Alert color="danger" variant="soft" startDecorator={<WarningIcon />}>
          {t('_accessKeyActions.selectedAccessKeyInstanceNotAvailable')}
        </Alert>
      )}
      <FormControl className="min-w-0 flex-1">
        <FormLabel required>{t('type')}</FormLabel>
        <Select
          value={values.actionType}
          onChange={(_, value) => {
            setFieldValue('actionType', value);
            setFieldValue('accessKeys', []);
            setFieldValue('borrower', null);
            setFieldValue('caseId', null);
          }}
        >
          {allowedActionTypes.map((actionType: AccessKeyActionType) => (
            <Option key={actionType} value={actionType}>
              {t(`_accessKeyActions.type.${lowerFirst(actionType)}`)}
            </Option>
          ))}
        </Select>
      </FormControl>

      {values.actionType !== AccessKeyActionType.Return && (
        <div className="flex flex-row gap-4">
          <FormikDatePicker
            options={{
              disablePast: true,
              format: 'YYYY-MM-DD',
            }}
            disabled={values.actionType === AccessKeyActionType.Loan}
            clearable
            className="min-w-0 flex-1"
            name="fromDate"
            label={t('fromDate')}
            required
          />
          <FormikDatePicker
            options={{
              disablePast: true,
              format: 'YYYY-MM-DD',
            }}
            clearable
            className="min-w-0 flex-1"
            name="untilDate"
            label={t('untilDate')}
            required
          />
        </div>
      )}
      <div className="flex flex-row gap-4">
        <Button
          onClick={() => {
            toggles.selectAccessKeys.show();
            setFieldTouched('accessKeys');
          }}
          className="flex-1"
          disabled={
            (values.actionType === AccessKeyActionType.Reservation &&
              (!values.fromDate || !values.untilDate)) ||
            !values.actionType
          }
        >
          {t('_accessKeyActions.selectAccessKeys')} ({values.accessKeys.length})
        </Button>
      </div>
      {values.actionType !== AccessKeyActionType.Return && (
        <FormikCrmSelectorJoy
          startDecorator={<UserIcon />}
          isRequired={
            values.actionType === AccessKeyActionType.Reservation ||
            values.actionType === AccessKeyActionType.Loan
          }
          name="borrower"
          label={t('_accessKeyActions.borrower')}
        />
      )}
      {values.actionType !== AccessKeyActionType.Return && (
        <FormikCaseSelector
          name="caseId"
          label={t('case')}
          caseType={CaseType.Case}
          skipInitialOnChange
        />
      )}
      <FormikTextField name="remark" label={t('remark')} />
      {toggles.selectAccessKeys.value && (
        <AddAccessKeyActionSelectAccessKeysModal
          toggle={toggles.selectAccessKeys}
          selectedAccessKeyInstanceId={selectedAccessKeyInstanceId}
          actionGroupId={actionGroupId}
        />
      )}
    </>
  );
};

export default AddAccessKeyActionForm;
